/**
 * React Query Hooks for API Data Fetching
 * automatic caching, deduplication, and refetching
 */

import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import {
  dashboardApi,
  categoryApi,
  locationApi,
  authApi,
  inventoryApi,
  stockApi,
  usersApi,
  reportsApi,
} from '@/services/api';

// Query Keys - Centralized for consistency
export const queryKeys = {
  // Dashboard
  dashboard: {
    stats: ['dashboard', 'stats'] as const,
    stockMovement: (days: number) => ['dashboard', 'stockMovement', days] as const,
    recentTransactions: (limit: number) => ['dashboard', 'recentTransactions', limit] as const,
    lowStockAlerts: ['dashboard', 'lowStockAlerts'] as const,
  },
  // Categories
  categories: (params?: { search?: string; page?: number; per_page?: number }) =>
    ['categories', params] as const,
  category: (id: number) => ['category', id] as const,
  // Locations
  locations: (params?: { search?: string; page?: number; per_page?: number }) =>
    ['locations', params] as const,
  location: (id: number) => ['location', id] as const,
  // Inventory
  inventory: (params?: any) => ['inventory', params] as const,
  inventoryItem: (id: number) => ['inventory', 'item', id] as const,
  inventoryBarcode: (barcode: string) => ['inventory', 'barcode', barcode] as const,
  inventoryLowStock: (params?: any) => ['inventory', 'lowStock', params] as const,
  // Stock Transactions
  stockTransactions: (params?: any) => ['stock', 'transactions', params] as const,
  stockTransaction: (id: number) => ['stock', 'transaction', id] as const,
  stockItemHistory: (itemId: number, params?: any) => ['stock', 'itemHistory', itemId, params] as const,
  // Users
  users: (params?: any) => ['users', params] as const,
  user: (id: number) => ['user', id] as const,
  // Auth
  currentUser: ['auth', 'currentUser'] as const,
  // Reports
  reports: {
    inventory: (params?: any) => ['reports', 'inventory', params] as const,
    transactions: (params?: any) => ['reports', 'transactions', params] as const,
    stockValue: (params?: any) => ['reports', 'stockValue', params] as const,
    movementSummary: (params?: any) => ['reports', 'movementSummary', params] as const,
    revenue: (params: any) => ['reports', 'revenue', params] as const,
    sales: (params: any) => ['reports', 'sales', params] as const,
    discounts: (params: any) => ['reports', 'discounts', params] as const,
    activity: (params: any) => ['reports', 'activity', params] as const,
    utilization: (params: any) => ['reports', 'utilization', params] as const,
    utilizationDetail: (params: any) => ['reports', 'utilization', 'detail', params] as const,
    stockBatches: (params: any) => ['reports', 'stockBatches', params] as const,
  },
};

//Dashboard Hooks 

export const useDashboardStats = () => {
  return useQuery({
    queryKey: queryKeys.dashboard.stats,
    queryFn: async () => {
      const response = await dashboardApi.getStats();
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - stats change frequently
  });
};

export const useStockMovement = (days: number = 7) => {
  return useQuery({
    queryKey: queryKeys.dashboard.stockMovement(days),
    queryFn: async () => {
      const response = await dashboardApi.getStockMovement(days);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useRecentTransactions = (limit: number = 10) => {
  return useQuery({
    queryKey: queryKeys.dashboard.recentTransactions(limit),
    queryFn: async () => {
      const response = await dashboardApi.getRecentTransactions(limit);
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute - recent transactions change often
  });
};

export const useLowStockAlerts = () => {
  return useQuery({
    queryKey: queryKeys.dashboard.lowStockAlerts,
    queryFn: async () => {
      const response = await dashboardApi.getLowStockAlerts();
      return response.data;
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
  });
};

//Category Hooks

export const useCategories = (params?: { search?: string; page?: number; per_page?: number; status?: 'active' | 'inactive' }) => {
  return useQuery({
    queryKey: queryKeys.categories(params),
    queryFn: async () => {
      const response = await categoryApi.getCategories(params);
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - categories don't change often
  });
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; description?: string; is_active?: boolean }) => {
      return await categoryApi.createCategory(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { name?: string; description?: string; is_active?: boolean } }) => {
      return await categoryApi.updateCategory(id, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
      queryClient.invalidateQueries({ queryKey: queryKeys.category(variables.id) });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      return await categoryApi.deleteCategory(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
    },
  });
};

export const useToggleCategoryActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      return await categoryApi.toggleCategoryActive(id);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
      queryClient.invalidateQueries({ queryKey: queryKeys.category(id) });
    },
  });
};

export const useCategory = (id: number) => {
  return useQuery({
    queryKey: queryKeys.category(id),
    queryFn: async () => {
      const response = await categoryApi.getCategory(id);
      return response.data;
    },
    enabled: !!id, // Only fetch if id exists
  });
};

//Location Hooks

export const useLocations = (params?: { search?: string; page?: number; per_page?: number; type?: 'receiving' | 'issuing'; enabled?: boolean }) => {
  const { enabled = true, ...queryParams } = params || {};
  return useQuery({
    queryKey: queryKeys.locations(queryParams),
    queryFn: async () => {
      const response = await locationApi.getLocations(queryParams);
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - locations don't change often
    enabled: enabled,
  });
};

export const useLocation = (id: number) => {
  return useQuery({
    queryKey: queryKeys.location(id),
    queryFn: async () => {
      const response = await locationApi.getLocation(id);
      return response.data;
    },
    enabled: !!id,
  });
};

// Inventory Hooks 

export const useInventory = (params?: any) => {
  return useQuery({
    queryKey: queryKeys.inventory(params),
    queryFn: async () => {
      const response = await inventoryApi.getItems(params);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useInventoryItem = (id: number) => {
  return useQuery({
    queryKey: queryKeys.inventoryItem(id),
    queryFn: async () => {
      const response = await inventoryApi.getItem(id);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useInventoryByBarcode = (barcode: string) => {
  return useQuery({
    queryKey: queryKeys.inventoryBarcode(barcode),
    queryFn: async () => {
      const response = await inventoryApi.searchByBarcode(barcode);
      return response.data;
    },
    enabled: !!barcode && barcode.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - barcode lookups can be cached
  });
};

export const useLowStockItems = (params?: any) => {
  return useQuery({
    queryKey: queryKeys.inventoryLowStock(params),
    queryFn: async () => {
      const response = await inventoryApi.getItems({ ...params, low_stock: true });
      return response.data;
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
  });
};

// Stock Transaction Hooks 

export const useStockTransactions = (params?: any) => {
  return useQuery({
    queryKey: queryKeys.stockTransactions(params),
    queryFn: async () => {
      const response = await stockApi.getTransactions(params);
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Note: getItemHistory API method not yet implemented
// export const useStockItemHistory = (itemId: number, params?: any) => {
//   return useQuery({
//     queryKey: queryKeys.stockItemHistory(itemId, params),
//     queryFn: async () => {
//       const response = await stockApi.getItemHistory(itemId, params);
//       return response.data;
//     },
//     enabled: !!itemId,
//     staleTime: 2 * 60 * 1000, // 2 minutes
//   });
// };

// User Hooks 

export const useCurrentUser = () => {
  return useQuery({
    queryKey: queryKeys.currentUser,
    queryFn: async () => {
      const user = await authApi.getCurrentUser();
      return user;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry auth failures
  });
};

export const useUsers = (params?: any & { enabled?: boolean }) => {
  const { enabled, ...queryParams } = params || {};
  return useQuery({
    queryKey: queryKeys.users(queryParams),
    queryFn: async () => {
      const response = await usersApi.getUsers(queryParams);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: enabled !== false, // Default to true, but can be disabled
  });
};

export const useUser = (id: number) => {
  return useQuery({
    queryKey: queryKeys.user(id),
    queryFn: async () => {
      const response = await usersApi.getUser(id);
      return response.data;
    },
    enabled: !!id,
  });
};

// Mutation Hooks 

// Inventory Mutations
export const useCreateInventoryItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => inventoryApi.createItem(data),
    onSuccess: () => {
      // Invalidate inventory queries to refetch
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
    },
  });
};

export const useUpdateInventoryItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => inventoryApi.updateItem(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItem(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
    },
  });
};

export const useDeleteInventoryItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => inventoryApi.deleteItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
    },
  });
};

// Stock Transaction Mutations
export const useReceiveStock = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => stockApi.receiveStock(data),
    onSuccess: () => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['stock', 'transactions'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.recentTransactions(10) });
      // Invalidate all stock movement queries (regardless of days parameter)
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'stockMovement'] });
    },
  });
};

export const useIssueStock = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => stockApi.issueStock(data),
    onSuccess: () => {
      // Invalidate so inventory, dashboard, reports and stock-by-location all refetch
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['stock'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.recentTransactions(10) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.lowStockAlerts });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'stockMovement'] });
    },
  });
};

// Location Mutations
export const useCreateLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => locationApi.createLocation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
};

export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => locationApi.updateLocation(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.location(variables.id) });
    },
  });
};

export const useDeleteLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => locationApi.deleteLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
};

// Category Mutations
// Note: Category CRUD API methods (create, update, delete) not yet implemented
// Uncomment when API methods are available:
// export const useCreateCategory = () => {
//   const queryClient = useQueryClient();
//   
//   return useMutation({
//     mutationFn: (data: any) => categoryApi.createCategory(data),
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['categories'] });
//     },
//   });
// };

// export const useUpdateCategory = () => {
//   const queryClient = useQueryClient();
//   
//   return useMutation({
//     mutationFn: ({ id, data }: { id: number; data: any }) => categoryApi.updateCategory(id, data),
//     onSuccess: (_, variables) => {
//       queryClient.invalidateQueries({ queryKey: ['categories'] });
//       queryClient.invalidateQueries({ queryKey: queryKeys.category(variables.id) });
//     },
//   });
// };

// export const useDeleteCategory = () => {
//   const queryClient = useQueryClient();
//   
//   return useMutation({
//     mutationFn: (id: number) => categoryApi.deleteCategory(id),
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['categories'] });
//     },
//   });
// };

// User Mutations
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => usersApi.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => usersApi.updateUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.user(variables.id) });
      // If updating current user, invalidate current user query
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => usersApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

// ─── Report Hooks ─────────────────────────────────────────────────────────────
// staleTime: 10 min — historical report data is stable within a session
// gcTime:    60 min — keep generated reports in memory for an hour
const REPORT_STALE = 10 * 60 * 1000;
const REPORT_GC    = 60 * 60 * 1000;

export const useRevenueReport = (params: {
  date_from: string; date_to: string; group_by: 'day' | 'month';
} | null) => {
  return useQuery({
    queryKey: queryKeys.reports.revenue(params),
    queryFn: async () => {
      const res = await reportsApi.getRevenueReport(params!);
      return (res as any).data ?? res;
    },
    enabled: !!params,
    staleTime: REPORT_STALE,
    gcTime: REPORT_GC,
    retry: 1,
  });
};

export const useSalesReport = (params: {
  date_from: string; date_to: string;
  view: 'summary' | 'detail' | 'by_category';
  per_page: number; page: number;
} | null) => {
  return useQuery({
    queryKey: queryKeys.reports.sales(params),
    queryFn: async () => {
      const res = await reportsApi.getSalesReport(params!);
      return res;
    },
    enabled: !!params,
    staleTime: REPORT_STALE,
    gcTime: REPORT_GC,
    retry: 1,
  });
};

export const useDiscountsReport = (params: {
  date_from: string; date_to: string; per_page: number; page: number;
} | null) => {
  return useQuery({
    queryKey: queryKeys.reports.discounts(params),
    queryFn: async () => {
      const res = await reportsApi.getDiscountsReport(params!);
      return res;
    },
    enabled: !!params,
    staleTime: REPORT_STALE,
    gcTime: REPORT_GC,
    retry: 1,
  });
};

export const useActivityReport = (params: {
  date_from: string; date_to: string;
  type?: 'receive' | 'issue';
  per_page: number; page: number;
} | null) => {
  return useQuery({
    queryKey: queryKeys.reports.activity(params),
    queryFn: async () => {
      const res = await stockApi.getTransactions(params!);
      return res;
    },
    enabled: !!params,
    staleTime: REPORT_STALE,
    gcTime: REPORT_GC,
    retry: 1,
  });
};

export const useUtilizationSummaryReport = (params: {
  date_from: string; date_to: string; location_id?: number;
} | null) => {
  return useQuery({
    queryKey: queryKeys.reports.utilization(params),
    queryFn: async () => {
      const res = await reportsApi.getUtilizationReport(params!);
      return (res as any).data ?? res;
    },
    enabled: !!params,
    staleTime: REPORT_STALE,
    gcTime: REPORT_GC,
    retry: 1,
  });
};

export const useUtilizationDetailReport = (params: {
  date_from: string; date_to: string;
  location_id?: number; per_page: number; page: number;
} | null) => {
  return useQuery({
    queryKey: queryKeys.reports.utilizationDetail(params),
    queryFn: async () => {
      const res = await reportsApi.getUtilizationDetailReport(params!);
      return res;
    },
    enabled: !!params,
    staleTime: REPORT_STALE,
    gcTime: REPORT_GC,
    retry: 1,
  });
};

export const useStockBatchReport = (params: {
  location_id?: number;
  item_search?: string;
  batch_number?: string;
  include_empty?: boolean;
  page: number;
  per_page: number;
} | null) => {
  return useQuery({
    queryKey: queryKeys.reports.stockBatches(params),
    queryFn: async () => {
      const res = await reportsApi.getStockBatchReport(params!);
      return res;
    },
    enabled: !!params,
    staleTime: REPORT_STALE,
    gcTime: REPORT_GC,
    retry: 1,
  });
};

//Prefetching Functions

/**
 * Prefetch all critical data after login
 * This runs in parallel and doesn't block navigation
 */
export const prefetchAllData = async (queryClient: QueryClient) => {
  const prefetchPromises = [
    // Dashboard data
    queryClient.prefetchQuery({
      queryKey: queryKeys.dashboard.stats,
      queryFn: async () => {
        const response = await dashboardApi.getStats();
        return response.data;
      },
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.dashboard.stockMovement(7),
      queryFn: async () => {
        const response = await dashboardApi.getStockMovement(7);
        return response.data;
      },
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.dashboard.recentTransactions(10),
      queryFn: async () => {
        const response = await dashboardApi.getRecentTransactions(10);
        return response.data;
      },
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.dashboard.lowStockAlerts,
      queryFn: async () => {
        const response = await dashboardApi.getLowStockAlerts();
        return response.data;
      },
    }),
    // Common dropdown data
    queryClient.prefetchQuery({
      queryKey: queryKeys.categories({ per_page: 100 }),
      queryFn: async () => {
        const response = await categoryApi.getCategories({ per_page: 100 });
        return response.data;
      },
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.locations({ per_page: 100 }),
      queryFn: async () => {
        const response = await locationApi.getLocations({ per_page: 100 });
        return response.data;
      },
    }),
    // User data
    queryClient.prefetchQuery({
      queryKey: queryKeys.currentUser,
      queryFn: async () => {
        return await authApi.getCurrentUser();
      },
    }),
  ];

  // Execute all prefetches in parallel
  await Promise.allSettled(prefetchPromises);
  
  if (import.meta.env.DEV) {
    console.log('✅ All data prefetched with React Query');
  }
};

